import { useMemo } from 'react'
import { isArray, isEmpty } from 'lodash-es'
import { COMPANY_REQUIRED_DOCUMENTS } from '../helpers/constants'
import {
  CustomerDocument,
  DocumentRequirement,
  DocumentType,
  MergedDocument,
  RequiredButNotUploadedDocument,
  RequiredDocument,
  UploadedCustomerDocument,
} from '../helpers/types'

const getIndividualRequiredDocuments = (
  infoRequiredDocuments: DocumentRequirement[]
): RequiredDocument[] => {
  if (isEmpty(infoRequiredDocuments)) return []
  return infoRequiredDocuments.map(doc => ({
    type: doc.types,
    subType: doc.docSetType,
    nameKey: doc.docSetType.toLowerCase(),
  }))
}

const getRequiredType = (type?: DocumentType): string => {
  if (!type) return ''
  if (isArray(type)) return type?.[0]
  return type
}

const initialRequiredDocuments = (
  reqDoc: RequiredDocument,
  isCompany: boolean
): CustomerDocument => {
  const { type, subType = '' } = reqDoc || {}
  const requiredType = getRequiredType(type)
  const resolvedSubType = isCompany ? subType : subType || requiredType

  return {
    reference: '',
    externalReference: '',
    type: requiredType,
    name: requiredType,
    description: '',
    status: 'PENDING',
    subType: resolvedSubType,
  }
}

const listRequiredDocuments = (isCompany: boolean, infoRequiredDocuments: DocumentRequirement[]) =>
  isCompany ? COMPANY_REQUIRED_DOCUMENTS : getIndividualRequiredDocuments(infoRequiredDocuments)

const isOtherStandalone = (reqDoc: RequiredDocument) => {
  const { type } = reqDoc || {}
  return type === 'OTHER'
}

const buildNotUploadedRequired = (
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
): RequiredButNotUploadedDocument[] =>
  listRequiredDocuments(isCompany, infoRequiredDocuments)
    .filter(reqDoc => !isOtherStandalone(reqDoc))
    .map(reqDoc => ({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false }))

const countBaseRequired = (
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
): number =>
  listRequiredDocuments(isCompany, infoRequiredDocuments)?.filter(({ type }) => type !== 'OTHER')
    ?.length

const findMatchingReceivedDoc = (
  reqDoc: RequiredDocument,
  isCompany: boolean,
  receivedDocsMap: Map<string, CustomerDocument>
): CustomerDocument | undefined => {
  const { type, subType = '' } = reqDoc || {}
  const typesArray = isArray(type) ? type : [type]
  const keys = (isCompany ? [subType] : typesArray)?.filter(Boolean)

  const matchedDocumentKey = keys?.find(key => receivedDocsMap?.has(key))
  return matchedDocumentKey ? receivedDocsMap?.get(matchedDocumentKey) : undefined
}

const getReceivedDocuments = (receivedDocuments: CustomerDocument[], isCompany: boolean) => {
  const receivedDocsMap = new Map<string, CustomerDocument>()
  const otherUploadedDocs: CustomerDocument[] = []

  for (const doc of receivedDocuments) {
    if (doc?.type === 'OTHER') {
      otherUploadedDocs?.push(doc)
      continue
    }
    const documentKey = isCompany ? doc?.subType : doc?.type
    if (documentKey) {
      receivedDocsMap?.set(documentKey, doc)
    }
  }

  return { receivedDocsMap, otherUploadedDocs }
}

const getOtherDocuments = (
  receivedDocsMap: Map<string, CustomerDocument>,
  otherUploadedDocs: CustomerDocument[],
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
) => {
  const uploadedDocs: UploadedCustomerDocument[] = []
  const requiredDocs: RequiredButNotUploadedDocument[] = []
  const requiredDocuments = listRequiredDocuments(isCompany, infoRequiredDocuments)

  for (const reqDoc of requiredDocuments) {
    const match = findMatchingReceivedDoc(reqDoc, isCompany, receivedDocsMap)

    if (match) {
      uploadedDocs?.push({ ...match, isUploaded: true })
      continue
    }
    if (isOtherStandalone(reqDoc)) continue
    requiredDocs?.push({ ...initialRequiredDocuments(reqDoc, isCompany), isUploaded: false })
  }

  if (!isEmpty(otherUploadedDocs)) {
    uploadedDocs?.push(
      ...otherUploadedDocs.map((doc): UploadedCustomerDocument => ({ ...doc, isUploaded: true }))
    )
  }

  return { uploadedDocs, requiredDocs }
}

export const useMergedDocuments = (
  receivedDocuments: CustomerDocument[],
  isCompany: boolean,
  infoRequiredDocuments: DocumentRequirement[]
) => {
  return useMemo(() => {
    if (!receivedDocuments || isEmpty(receivedDocuments)) {
      const mergedDocuments: RequiredButNotUploadedDocument[] = buildNotUploadedRequired(
        isCompany,
        infoRequiredDocuments
      )

      const individualRequiredDocuments = getIndividualRequiredDocuments(infoRequiredDocuments)

      const requiredCount = listRequiredDocuments(isCompany, infoRequiredDocuments)?.filter(
        reqDoc => reqDoc?.type !== 'OTHER'
      ).length
      return {
        mergedDocuments,
        uploadedCount: 0,
        requiredCount,
        getIndividualRequiredDocuments,
        individualRequiredDocuments,
      }
    }

    if (
      (!isCompany && !isEmpty(receivedDocuments) && !isEmpty(infoRequiredDocuments)) ||
      !infoRequiredDocuments ||
      (isEmpty(infoRequiredDocuments) && !!receivedDocuments && !isCompany)
    ) {
      return {
        mergedDocuments: receivedDocuments.map(doc => ({ ...doc, isUploaded: true })),
        uploadedCount: receivedDocuments.length,
        requiredCount: 0,
        getIndividualRequiredDocuments,
      }
    }
    const { receivedDocsMap, otherUploadedDocs } = getReceivedDocuments(
      receivedDocuments,
      isCompany
    )
    const { uploadedDocs, requiredDocs } = getOtherDocuments(
      receivedDocsMap,
      otherUploadedDocs,
      isCompany,
      infoRequiredDocuments
    )

    const mergedDocuments: MergedDocument[] = [...uploadedDocs, ...requiredDocs]

    const uploadedCount = uploadedDocs?.length
    const baseRequiredTotal = countBaseRequired(isCompany, infoRequiredDocuments)

    const requiredCount = baseRequiredTotal + (otherUploadedDocs?.length > 0 ? 1 : 0)

    return { mergedDocuments, uploadedCount, requiredCount, getIndividualRequiredDocuments }
  }, [receivedDocuments, isCompany, infoRequiredDocuments])
}
